import { Link } from '@tanstack/react-router';
import { ExternalLink, House, Search } from 'lucide-react';
import { useImperativeHandle, useRef, useState, type ReactNode, type Ref } from 'react';
import { Popover } from '../../../../components/Popover';
import type { AnchorRect } from '../../../../lib/anchored-panel';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import { isSectionActive, type AdminGroup, type AdminMenuView } from '../../admin-sections';
import type { AdminMenuCounts } from '../../use-admin-menu-counts';
import { AdminFind } from './AdminFind';
import { MenuCount } from './MenuCount';
import { MENU_ITEM_ATTRIBUTE, moveMenuFocus } from './roving-focus';
import styles from './AdminRail.module.css';

/** The group pop-up's width in admin-frame.html (Option A at 1024): a name and a one-line hint. */
const FLYOUT_WIDTH = 290;
const FIND_WIDTH = 360;
/** The gap between the rail and the pop-up that opens beside it. */
const BESIDE_GAP = 8;
/** A press on the open group's own icon closes it; the click that follows must not open it again. */
const REOPEN_GUARD_MS = 300;

export interface AdminRailHandle {
  /** Ctrl+/ at laptop width: opens "Find a setting" beside the rail. */
  openFind(): void;
}

interface AdminRailProps {
  readonly menu: AdminMenuView;
  readonly counts: AdminMenuCounts;
  readonly pathname: string;
  readonly ref?: Ref<AdminRailHandle>;
}

type Flyout = { readonly kind: 'find' } | { readonly kind: 'group'; readonly group: AdminGroup };

interface OpenFlyout {
  readonly flyout: Flyout;
  readonly anchor: AnchorRect;
  readonly trigger: HTMLElement;
}

/** Opens to the right of the trigger, its top level with the trigger's — beside the icon, never below it. */
function besideOf(element: HTMLElement): AnchorRect {
  const rect = element.getBoundingClientRect();
  const left = rect.right + BESIDE_GAP;
  return { top: rect.bottom, bottom: rect.top - BESIDE_GAP / 2, left, right: left };
}

/**
 * At laptop width the menu becomes a 72px icon rail (FRAME-SPEC §5): Find, Overview and one icon per group. A
 * group's sections open as a pop-up beside its icon.
 */
export function AdminRail({ menu, counts, pathname, ref }: AdminRailProps): ReactNode {
  const [open, setOpen] = useState<OpenFlyout | null>(null);
  const findButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastClosed = useRef<{ readonly trigger: HTMLElement; readonly at: number } | null>(null);

  const show = (flyout: Flyout, trigger: HTMLElement): void => {
    const recent = lastClosed.current;
    if (recent && recent.trigger === trigger && performance.now() - recent.at < REOPEN_GUARD_MS) return;
    setOpen({ flyout, anchor: besideOf(trigger), trigger });
  };

  const close = (returnFocus: boolean): void => {
    if (open) lastClosed.current = { trigger: open.trigger, at: performance.now() };
    setOpen(null);
    if (returnFocus) open?.trigger.focus();
  };

  useImperativeHandle(ref, () => ({
    openFind: () => {
      if (findButtonRef.current) setOpen({ flyout: { kind: 'find' }, anchor: besideOf(findButtonRef.current), trigger: findButtonRef.current });
    },
  }));

  const activeGroup = menu.groups.find((group) => group.sections.some((section) => isSectionActive(pathname, section)));
  const isOverviewActive = menu.overview !== null && isSectionActive(pathname, menu.overview);
  const isOpenFor = (kind: string, groupId?: string): boolean =>
    open !== null && open.flyout.kind === kind && (open.flyout.kind !== 'group' || open.flyout.group.id === groupId);

  return (
    <>
      <nav
        className={styles.rail}
        aria-label="Admin"
        onKeyDown={(event) => {
          moveMenuFocus(event);
        }}
      >
        <button
          ref={findButtonRef}
          type="button"
          className={styles.item}
          aria-haspopup="dialog"
          aria-expanded={isOpenFor('find')}
          tabIndex={0}
          onClick={(event) => show({ kind: 'find' }, event.currentTarget)}
          {...{ [MENU_ITEM_ATTRIBUTE]: '' }}
        >
          <Search size={20} aria-hidden />
          Find
        </button>

        {menu.overview && (
          <Link
            to={menu.overview.to}
            className={styles.item}
            aria-current={isOverviewActive ? 'page' : undefined}
            activeOptions={{ exact: true }}
            activeProps={{ 'aria-current': isOverviewActive ? 'page' : undefined }}
            tabIndex={-1}
            {...{ [MENU_ITEM_ATTRIBUTE]: '' }}
          >
            <House size={20} aria-hidden />
            Overview
          </Link>
        )}

        {menu.groups.map((group) => {
          const Icon = group.icon;
          const toDo = group.sections.reduce((sum, section) => sum + (counts[section.id] ?? 0), 0);
          return (
            <button
              key={group.id}
              type="button"
              className={styles.item}
              data-active={group === activeGroup || undefined}
              aria-haspopup="dialog"
              aria-expanded={isOpenFor('group', group.id)}
              aria-label={group.label}
              tabIndex={-1}
              onClick={(event) => show({ kind: 'group', group }, event.currentTarget)}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowRight') return;
                event.preventDefault();
                show({ kind: 'group', group }, event.currentTarget);
              }}
              {...{ [MENU_ITEM_ATTRIBUTE]: '' }}
            >
              <Icon size={20} aria-hidden />
              <span aria-hidden>{group.railLabel}</span>
              <MenuCount count={toDo} label={`${toDo} to do in ${group.label}`} />
            </button>
          );
        })}
      </nav>

      <Popover
        anchor={open?.anchor ?? null}
        onClose={() => close(true)}
        width={open?.flyout.kind === 'find' ? FIND_WIDTH : FLYOUT_WIDTH}
        label={open?.flyout.kind === 'group' ? open.flyout.group.label : 'Find a setting'}
      >
        {open?.flyout.kind === 'group' && (
          <GroupList group={open.flyout.group} counts={counts} pathname={pathname} onNavigate={() => close(false)} />
        )}
        {open?.flyout.kind === 'find' && <AdminFind menu={menu} isInline autoFocus onOpened={() => close(false)} />}
      </Popover>
    </>
  );
}

interface GroupListProps {
  readonly group: AdminGroup;
  readonly counts: AdminMenuCounts;
  readonly pathname: string;
  readonly onNavigate: () => void;
}

/** A group's sections beside its icon, each with its one-line hint. The first takes the focus when it opens. */
function GroupList({ group, counts, pathname, onNavigate }: GroupListProps): ReactNode {
  const firstRef = useRef<HTMLAnchorElement | null>(null);
  useFocusOnOpen(firstRef);

  return (
    <div
      className={styles.flyout}
      onKeyDown={(event) => {
        moveMenuFocus(event);
      }}
    >
      <div className={styles.flyoutHead}>{group.label}</div>
      {group.sections.map((section, index) => {
        const isActive = isSectionActive(pathname, section);
        const count = counts[section.id];
        const body = (
          <>
            <span className={styles.flyoutText}>
              <span className={styles.flyoutLabel}>
                {section.label}
                {section.isExternal === true && <ExternalLink size={12} aria-hidden />}
              </span>
              <span className={styles.flyoutHint}>{section.hint}</span>
            </span>
            <MenuCount count={count} label={count !== undefined && section.countLabel ? section.countLabel(count) : ''} />
          </>
        );
        const shared = {
          className: styles.flyoutItem,
          onClick: onNavigate,
          [MENU_ITEM_ATTRIBUTE]: '',
        };

        if (section.isExternal === true) {
          return (
            <a key={section.id} ref={index === 0 ? firstRef : undefined} href={section.to} target="_blank" rel="noreferrer" {...shared}>
              {body}
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          );
        }
        return (
          <Link
            key={section.id}
            ref={index === 0 ? firstRef : undefined}
            to={section.to}
            aria-current={isActive ? 'page' : undefined}
            activeOptions={{ exact: true }}
            activeProps={{ 'aria-current': isActive ? 'page' : undefined }}
            {...shared}
          >
            {body}
          </Link>
        );
      })}
    </div>
  );
}
